'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Button, Modal } from '@common/components';
import SvgDelete from '@common/icon/SvgDelete';
import { cn } from '@core/utils';
import { motion, type MotionProps, useReducedMotion } from 'framer-motion';
import type { ReactNode } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useOverlayHistory } from '@/hooks/useOverlayHistory';
import type { AdTriggerPhase } from '@/modules/adpopcorn-ssp';
import { AD_TRIGGER_COOLDOWN_NOTICE, AD_TRIGGER_PENDING_NOTICE } from '../../../_utils/adTriggerNotice';
import { ROULETTE_BLANK_IMAGE_SRC, ROULETTE_MAX_RETRY_COUNT } from '../_constants';
import type { RouletteSpinResult } from '../_hooks/useRouletteSpin';
import { resolveRouletteRetryType } from '../_utils/resolveRouletteState';

//* 시안 프로토타입(당첨·미당첨 애니메이션) 키프레임 실측값.
const GLOW_MOTION = {
  initial: {
    opacity: 0,
    scale: 0.32
  },
  animate: {
    opacity: 1,
    scale: 1
  },
  transition: {
    duration: 0.9,
    ease: 'linear'
  }
} satisfies MotionProps;
const WON_ITEM_MOTION = {
  initial: {
    scale: 0
  },
  animate: {
    scale: 1
  },
  transition: {
    duration: 0.9,
    ease: [0.4, 1.75, 0.3, 1]
  }
} satisfies MotionProps;
const settleSpring = (progress: number) => 1 - Math.exp(-progress * 7.2105) * (Math.cos(progress * 20.3943) + 0.3536 * Math.sin(progress * 20.3943));
const BLANK_DROP_MOTION = {
  initial: {
    opacity: 0,
    y: -116,
    scaleX: 1,
    scaleY: 1
  },
  animate: {
    opacity: [0, 0, 1, 1],
    y: [-116, -116, 4, 0, -4],
    scaleX: [1, 1, 1.2, 1],
    scaleY: [1, 1, 0.8, 1]
  },
  transition: {
    duration: 1.24,
    opacity: {
      duration: 1.24,
      times: [0, 0.024, 0.089, 1]
    },
    y: {
      duration: 1.24,
      times: [0, 0.024, 0.105, 0.129, 1],
      ease: ['linear', [0.58, 0.26, 1, 1], [0.15, 0.85, 0.3, 1], settleSpring]
    },
    scaleX: {
      duration: 1.24,
      times: [0, 0.105, 0.129, 1],
      ease: ['linear', [0.15, 0.85, 0.3, 1], settleSpring]
    },
    scaleY: {
      duration: 1.24,
      times: [0, 0.105, 0.129, 1],
      ease: ['linear', [0.15, 0.85, 0.3, 1], settleSpring]
    }
  }
} satisfies MotionProps;
const TEXT_BUTTON_CLASS_NAME = 'flex h-48 w-full items-center justify-center rounded-xxsmall font-700 text-Gray-600 typo-body3 transition-colors hover:text-Gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-Gray-300 active:text-Gray-800';
interface ResultLayoutProps {
  illustration: ReactNode;
  children: ReactNode;
  footer: ReactNode;
  onClose: () => void;
}
const CloseIconButton = t0 => {
  const $ = _c(3);
  const {
    onClose
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SvgDelete className="size-24" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onClose) {
    t2 = <button aria-label={"\uB2EB\uAE30"} onClick={onClose} className="-m-4 rounded-xxsmall p-4 text-Gray-900 transition-colors hover:bg-Gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-Gray-300 active:bg-Gray-200">{t1}</button>;
    $[1] = onClose;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
const ResultLayout = t0 => {
  const $ = _c(28);
  const {
    illustration,
    children,
    footer,
    onClose
  } = t0;
  const isDesktop = useMediaQuery(BREAKPOINTS.DESKTOP);
  if (isDesktop) {
    let t1;
    if ($[0] !== onClose) {
      t1 = <div className="flex justify-end px-4"><CloseIconButton onClose={onClose} /></div>;
      $[0] = onClose;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== children || $[3] !== illustration) {
      t2 = <div aria-live="polite" className="flex flex-col items-center gap-56 pt-92 text-center">{illustration}{children}</div>;
      $[2] = children;
      $[3] = illustration;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== footer) {
      t3 = <div className="mt-24 flex flex-col items-center gap-8">{footer}</div>;
      $[5] = footer;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
      t4 = <div className="flex w-[376px] flex-col">{t1}{t2}{t3}</div>;
      $[7] = t1;
      $[8] = t2;
      $[9] = t3;
      $[10] = t4;
    } else {
      t4 = $[10];
    }
    let t5;
    if ($[11] !== onClose || $[12] !== t4) {
      t5 = <Modal opened={true} onClose={onClose} className="overflow-x-hidden" wrapperClassName="[&>[role=dialog]]:bg-gradient-to-b [&>[role=dialog]]:from-B-25 [&>[role=dialog]]:via-Gray-white [&>[role=dialog]]:to-Gray-white">{t4}</Modal>;
      $[11] = onClose;
      $[12] = t4;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  let t1;
  let t2;
  let t3;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 0
    };
    t2 = {
      opacity: 1
    };
    t3 = {
      duration: 0.24,
      ease: "easeOut"
    };
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t1 = $[14];
    t2 = $[15];
    t3 = $[16];
  }
  let t4;
  if ($[17] !== onClose) {
    t4 = <div className="flex h-48 shrink-0 items-center px-20"><CloseIconButton onClose={onClose} /></div>;
    $[17] = onClose;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  let t5;
  if ($[19] !== children || $[20] !== illustration) {
    t5 = <div aria-live="polite" className="flex flex-1 flex-col items-center gap-56 px-20 pt-104 text-center">{illustration}{children}</div>;
    $[19] = children;
    $[20] = illustration;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== footer) {
    t6 = <div className="sticky bottom-0 flex flex-col items-center gap-8 bg-gradient-to-t from-Gray-white from-60% to-Gray-white/0 px-20 pb-20 pt-32">{footer}</div>;
    $[22] = footer;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== t4 || $[25] !== t5 || $[26] !== t6) {
    t7 = <motion.div role="dialog" aria-modal={true} initial={t1} animate={t2} transition={t3} className="fixed inset-0 z-40 flex flex-col overflow-y-auto break-keep bg-gradient-to-b from-B-25 via-Gray-white to-Gray-white"><div className="mx-auto flex w-full max-w-[750px] flex-1 flex-col">{t4}{t5}{t6}</div></motion.div>;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
};
interface RouletteResultScreenProps {
  spin: RouletteSpinResult;
  brandName?: string | null;
  adPhase: AdTriggerPhase;
  isRetrying: boolean;
  onFreeRetry: () => void;
  onAdRetry: () => void;
  onClose: () => void;
}
export const RouletteResultScreen = t0 => {
  const $ = _c(77);
  const {
    spin,
    brandName,
    adPhase,
    isRetrying,
    onFreeRetry,
    onAdRetry,
    onClose
  } = t0;
  useOverlayHistory(true, onClose);
  const isReducedMotion = useReducedMotion();
  if (spin.won) {
    let t1;
    if ($[0] !== isReducedMotion) {
      t1 = isReducedMotion ? {} : GLOW_MOTION;
      $[0] = isReducedMotion;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <motion.span aria-hidden={true} {...t1} className="absolute inset-0 rounded-pill bg-Y-100 blur-2xl" />;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== isReducedMotion || $[5] !== spin.resultImageUrl) {
      t3 = spin.resultImageUrl ? <motion.span {...isReducedMotion ? {} : WON_ITEM_MOTION} className="absolute inset-0 overflow-hidden rounded-xxlarge"><Image src={spin.resultImageUrl} alt="" fill={true} sizes="164px" className="object-contain" /></motion.span> : null;
      $[4] = isReducedMotion;
      $[5] = spin.resultImageUrl;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] !== t2 || $[8] !== t3) {
      t4 = <span className="relative flex size-164 items-center justify-center">{t2}{t3}</span>;
      $[7] = t2;
      $[8] = t3;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    let t5;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Button asChild={true} className="w-full"><CustomLink href={ROUTES.POINT_MARKET_HISTORY.pathname}>내 쿠폰함에서 확인하기</CustomLink></Button>;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== onClose) {
      t6 = <>{t5}<button onClick={onClose} className={TEXT_BUTTON_CLASS_NAME}>닫기</button></>;
      $[11] = onClose;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== brandName) {
      t7 = brandName ? <p className="font-600 text-Gray-600 typo-h7">{brandName}</p> : null;
      $[13] = brandName;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== spin.resultItemName) {
      t8 = <h2 className="font-700 text-Gray-800 typo-h6">{spin.resultItemName}</h2>;
      $[15] = spin.resultItemName;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== t7 || $[18] !== t8) {
      t9 = <div className="flex flex-col gap-4">{t7}{t8}</div>;
      $[17] = t7;
      $[18] = t8;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="whitespace-pre-line font-400 text-Gray-700 typo-body3">{"\uCFE0\uD3F0\uC774 \uB0B4 \uCFE0\uD3F0\uD568\uC5D0 \uB3C4\uCC29\uD588\uC5B4\uC694.\n\uBC1B\uC740 \uB0A0\uBD80\uD130 30\uC77C \uC548\uC5D0 \uC4F8 \uC218 \uC788\uC5B4\uC694."}</p>;
      $[20] = t10;
    } else {
      t10 = $[20];
    }
    let t11;
    if ($[21] !== t9) {
      t11 = <div className="flex flex-col gap-16">{t9}{t10}</div>;
      $[21] = t9;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== onClose || $[24] !== t11 || $[25] !== t4 || $[26] !== t6) {
      t12 = <ResultLayout onClose={onClose} illustration={t4} footer={t6}>{t11}</ResultLayout>;
      $[23] = onClose;
      $[24] = t11;
      $[25] = t4;
      $[26] = t6;
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    return t12;
  }
  let t1;
  if ($[28] !== spin) {
    t1 = resolveRouletteRetryType(spin);
    $[28] = spin;
    $[29] = t1;
  } else {
    t1 = $[29];
  }
  const retryType = t1;
  const adNotice = adPhase === "pending" ? AD_TRIGGER_PENDING_NOTICE : adPhase === "cooldown" ? AD_TRIGGER_COOLDOWN_NOTICE : null;
  let t2;
  if ($[30] !== isReducedMotion) {
    t2 = isReducedMotion ? {} : GLOW_MOTION;
    $[30] = isReducedMotion;
    $[31] = t2;
  } else {
    t2 = $[31];
  }
  let t3;
  if ($[32] !== t2) {
    t3 = <motion.span aria-hidden={true} {...t2} className="absolute inset-0 rounded-pill bg-Gray-500/50 blur-3xl" />;
    $[32] = t2;
    $[33] = t3;
  } else {
    t3 = $[33];
  }
  let t4;
  if ($[34] !== isReducedMotion) {
    t4 = isReducedMotion ? {} : BLANK_DROP_MOTION;
    $[34] = isReducedMotion;
    $[35] = t4;
  } else {
    t4 = $[35];
  }
  let t5;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Image src={ROULETTE_BLANK_IMAGE_SRC} alt="" width={164} height={164} preload={true} />;
    $[36] = t5;
  } else {
    t5 = $[36];
  }
  let t6;
  if ($[37] !== t4) {
    t6 = <motion.span {...t4} className="relative">{t5}</motion.span>;
    $[37] = t4;
    $[38] = t6;
  } else {
    t6 = $[38];
  }
  let t7;
  if ($[39] !== t3 || $[40] !== t6) {
    t7 = <span className="relative flex size-164 items-center justify-center">{t3}{t6}</span>;
    $[39] = t3;
    $[40] = t6;
    $[41] = t7;
  } else {
    t7 = $[41];
  }
  let t8;
  bb0: {
    if (retryType === "FREE") {
      let t9;
      if ($[42] !== isRetrying || $[43] !== onFreeRetry) {
        t9 = <Button className="w-full" loading={isRetrying} onClick={onFreeRetry}>한 번 더 돌리기</Button>;
        $[42] = isRetrying;
        $[43] = onFreeRetry;
        $[44] = t9;
      } else {
        t9 = $[44];
      }
      t8 = t9;
      break bb0;
    }
    if (retryType === "AD") {
      const t9 = !adNotice;
      const t10 = !adNotice;
      let t11;
      if ($[45] !== t10) {
        t11 = cn("text-Gray-500 typo-caption1", {
          invisible: t10
        });
        $[45] = t10;
        $[46] = t11;
      } else {
        t11 = $[46];
      }
      const t12 = adNotice ?? AD_TRIGGER_PENDING_NOTICE;
      let t13;
      if ($[47] !== t11 || $[48] !== t12 || $[49] !== t9) {
        t13 = <p aria-hidden={t9} className={t11}>{t12}</p>;
        $[47] = t11;
        $[48] = t12;
        $[49] = t9;
        $[50] = t13;
      } else {
        t13 = $[50];
      }
      const t14 = adPhase === "pending" || isRetrying;
      const t15 = adPhase === "cooldown";
      let t16;
      if ($[51] !== onAdRetry || $[52] !== t14 || $[53] !== t15) {
        t16 = <Button className="w-full" loading={t14} disabled={t15} onClick={onAdRetry}>광고 보고 한 번 더 참여하기</Button>;
        $[51] = onAdRetry;
        $[52] = t14;
        $[53] = t15;
        $[54] = t16;
      } else {
        t16 = $[54];
      }
      let t17;
      if ($[55] !== onClose) {
        t17 = <button onClick={onClose} className={TEXT_BUTTON_CLASS_NAME}>그만할게요</button>;
        $[55] = onClose;
        $[56] = t17;
      } else {
        t17 = $[56];
      }
      let t18;
      if ($[57] !== t13 || $[58] !== t16 || $[59] !== t17) {
        t18 = <>{t13}{t16}{t17}</>;
        $[57] = t13;
        $[58] = t16;
        $[59] = t17;
        $[60] = t18;
      } else {
        t18 = $[60];
      }
      t8 = t18;
      break bb0;
    }
    let t9;
    if ($[61] !== onClose) {
      t9 = <Button className="w-full" onClick={onClose}>닫기</Button>;
      $[61] = onClose;
      $[62] = t9;
    } else {
      t9 = $[62];
    }
    t8 = t9;
  }
  let t9;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h2 className="font-700 text-Gray-800 typo-h6">아쉽게도 당첨되지 않았어요.</h2>;
    $[63] = t9;
  } else {
    t9 = $[63];
  }
  const t10 = retryType === "FREE" ? "\uBB34\uB8CC\uB85C \uD55C \uBC88 \uB354 \uB3C4\uC804\uD560 \uC218 \uC788\uC5B4\uC694!" : retryType === "AD" ? "\uD55C \uBC88 \uB354 \uB3C4\uC804\uD574\uBCFC\uAE4C\uC694?\n\uAD11\uACE0\uB97C \uBCF4\uBA74 \uC81C\uD734\uD3EC\uC778\uD2B8 \uCD94\uAC00 \uC0AC\uC6A9 \uC5C6\uC774\n\uB8F0\uB81B\uC744 \uD55C \uBC88 \uB354 \uB3CC\uB9B4 \uC218 \uC788\uC5B4\uC694." : "\uC774\uBC88 \uCC38\uC5EC\uC758 \uC7AC\uB3C4\uC804 \uAE30\uD68C\uB97C \uBAA8\uB450 \uC0AC\uC6A9\uD588\uC5B4\uC694.";
  let t11;
  if ($[64] !== t10) {
    t11 = <div className="flex flex-col gap-16">{t9}<p className="whitespace-pre-line font-400 text-Gray-700 typo-body3">{t10}</p></div>;
    $[64] = t10;
    $[65] = t11;
  } else {
    t11 = $[65];
  }
  let t12;
  if ($[66] !== retryType || $[67] !== spin.remainingRetryCount) {
    t12 = retryType === "NONE" ? null : <Badge className="bg-M-25 text-M-400">남은 재도전 기회 {spin.remainingRetryCount}/{ROULETTE_MAX_RETRY_COUNT}</Badge>;
    $[66] = retryType;
    $[67] = spin.remainingRetryCount;
    $[68] = t12;
  } else {
    t12 = $[68];
  }
  let t13;
  if ($[69] !== t11 || $[70] !== t12) {
    t13 = <div className="flex flex-col items-center gap-12">{t11}{t12}</div>;
    $[69] = t11;
    $[70] = t12;
    $[71] = t13;
  } else {
    t13 = $[71];
  }
  let t14;
  if ($[72] !== onClose || $[73] !== t13 || $[74] !== t7 || $[75] !== t8) {
    t14 = <ResultLayout onClose={onClose} illustration={t7} footer={t8}>{t13}</ResultLayout>;
    $[72] = onClose;
    $[73] = t13;
    $[74] = t7;
    $[75] = t8;
    $[76] = t14;
  } else {
    t14 = $[76];
  }
  return t14;
};